import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const DEFAULT_SETTINGS: Record<string, boolean> = {
  nav_ai_projects_visible: false,
  nav_ai_crm_visible: false,
  nav_ai_hr_visible: false,
};

const QUERY_KEY = ['app-settings'];

export const useAppSettings = () => {
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<Record<string, any>> => {
      const { data, error } = await supabase
        .from('app_settings')
        .select('key, value');

      if (error) throw error;

      const settings: Record<string, any> = { ...DEFAULT_SETTINGS };
      (data || []).forEach((row) => {
        settings[row.key] = row.value;
      });
      return settings;
    },
    staleTime: 5 * 60 * 1000,
    placeholderData: DEFAULT_SETTINGS,
  });

  const getSetting = <T = any>(key: string, defaultValue?: T): T => {
    return (query.data?.[key] ?? defaultValue ?? DEFAULT_SETTINGS[key]) as T;
  };

  return {
    settings: query.data ?? DEFAULT_SETTINGS,
    isLoading: query.isLoading,
    getSetting,
    refetch: query.refetch,
  };
};

export const useUpdateAppSetting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: any }) => {
      const { error } = await supabase
        .from('app_settings')
        .upsert({ key, value, updated_at: new Date().toISOString() }, { onConflict: 'key' });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
};
